import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { takeEvery, put, apply } from 'redux-saga/effects';
import { call, select } from 'typed-redux-saga';
import { transformReturnShippingOptionDto } from 'data/transformers/return-shipping-option';
import { transformPickUpDatesDto } from 'data/transformers/pick-up-dates';
import { transformShippingInstructionsDto } from 'data/transformers/shipping-instructions';
import * as api from 'data/api';
import { HttpStatus, ResponseCode } from 'data/api/response-codes';
import { navigateToPage } from 'libs/utils/window';
import { tracker } from 'libs/common/tracker';
import { conversationReplyEvent, startConversationEvent, conversationHarassmentEvent, abTestExposeEvent } from 'libs/common/event-tracker/events';
import { getAbTestByName } from 'state/ab-tests/selectors';
import { getUserId, getExposee } from 'state/session/selectors';
import { transformTransaction } from 'data/transformers/transaction';
import { CONVERSATION_URL } from 'constants/routes/inbox';
import { AbTestVariant } from 'constants/abtest';
import * as statelessActions from './actions';
import { actions } from './slice';
import { transformTrackingJourneySummary, transformOfferRequestOptions, transformShipmentInstructions, transformDigitalLabel, transformConversationResponse } from './transformers';
import { getConversation as selectConversation, getWasHarassmentWarningOpen, getScoredHarassmentMessage, getScoredHarassmentMessageCorrelationId } from './selectors';

function* translateConversation(_ref) {
  let {
    payload: {
      conversationId,
      translate
    }
  } = _ref;
  const response = yield* call(api.translateConversation, {
    conversationId,
    translate
  });
  if ('errors' in response) return;
  yield put(actions.getConversationSuccess({
    conversation: transformConversationResponse(response.conversation),
    status: response.status
  }));
}

export function* getConversation(_ref2) {
  let {
    payload: {
      conversationId,
      fromPostReply = false
    }
  } = _ref2;
  yield put(actions.getConversationUiPending({
    fromPostReply
  }));

  if (Number.isNaN(Number(conversationId))) {
    // TODO: backend returns 200 when passed a string. Should be fixed in backend
    yield put(actions.getConversationUiFailure({
      error: null,
      status: HttpStatus.NotFound
    }));
    return;
  }

  const response = yield* call(api.getConversation, conversationId);
  const conversation = yield* select(selectConversation);
  const exposee = yield* select(getExposee);
  const firstTimeListerEducationAbTest = yield* select(getAbTestByName('first_time_lister_education')); // Braze conversations are updated synchronously, hence we need to avoid overriding Braze conversation in store

  if (conversation && !fromPostReply) return;

  if ('errors' in response) {
    var _response$errors$;

    yield put(actions.getConversationUiFailure({
      error: ((_response$errors$ = response.errors[0]) === null || _response$errors$ === void 0 ? void 0 : _response$errors$.value) || null,
      status: response.status
    }));
    return;
  }

  const transformedConversation = transformConversationResponse(response.conversation);

  if (transformedConversation.transactionId) {
    yield put(actions.fetchTransactionRequest({
      transactionId: transformedConversation.transactionId
    }));
  }

  yield put(actions.getConversationSuccess({
    conversation: transformedConversation,
    status: response.status
  }));

  if (transformedConversation.isFirstTimeListerEducationRequired) {
    if (!firstTimeListerEducationAbTest) return;
    const exposeEvent = abTestExposeEvent(_objectSpread(_objectSpread({}, exposee), firstTimeListerEducationAbTest));
    yield apply(tracker, tracker.track, [exposeEvent]);

    if (firstTimeListerEducationAbTest.variant !== AbTestVariant.Off) {
      yield put(actions.setFirstTimeListerEducationModalVisible({
        isVisible: true
      }));
    }
  }
}
export function* getTrackingJourneySummary(_ref3) {
  let {
    payload: {
      transactionId
    }
  } = _ref3;
  const response = yield* call(api.getTrackingJourneySummary, {
    transactionId
  });

  if ('errors' in response) {
    var _response$errors, _response$errors$2;

    yield put(actions.getTrackingJourneySummaryFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors = response.errors) === null || _response$errors === void 0 ? void 0 : (_response$errors$2 = _response$errors[0]) === null || _response$errors$2 === void 0 ? void 0 : _response$errors$2.value) || null
    }));
    return;
  }

  yield put(actions.getTrackingJourneySummarySuccess(transformTrackingJourneySummary(response.journey_summary)));
}
export function* getShipmentInstructions(_ref4) {
  let {
    payload: {
      transactionId
    }
  } = _ref4;
  const response = yield* call(api.getShipmentInstructions, {
    transactionId
  });
  if ('errors' in response) return;

  if (response.shipment_instructions) {
    yield put(actions.getShipmentInstructionsSuccess(transformShipmentInstructions(response.shipment_instructions)));
  }

  if (response.shipment_instructions.return_shipping_option) {
    yield put(actions.getReturnShipmentOptionSuccess(transformReturnShippingOptionDto(response.shipment_instructions.return_shipping_option)));
  }
}
export function* postReply(_ref5) {
  var _conversation$itemId;

  let {
    payload: {
      value,
      photoTempUuids
    }
  } = _ref5;
  const userId = yield* select(getUserId);
  const conversation = yield* select(selectConversation);
  const wasHarassmentWarningOpen = yield* select(getWasHarassmentWarningOpen);
  const scoredHarassmentMessage = yield* select(getScoredHarassmentMessage);
  const correlationId = yield* select(getScoredHarassmentMessageCorrelationId);
  if (!(conversation !== null && conversation !== void 0 && conversation.id) || !userId) return;
  const response = yield* call(api.postReply, {
    conversationId: conversation.id,
    body: value,
    photoTempUuids
  });

  if ((response === null || response === void 0 ? void 0 : response.code) === ResponseCode.HarassmentInMessageDetected && 'errors' in response) {
    var _response$payload$cor, _response$payload;

    yield put(actions.setHarassmentMessage({
      messageText: value || '',
      correlationId: (_response$payload$cor = (_response$payload = response.payload) === null || _response$payload === void 0 ? void 0 : _response$payload.correlation_id) !== null && _response$payload$cor !== void 0 ? _response$payload$cor : null
    }));
    return;
  }

  if ((response === null || response === void 0 ? void 0 : response.code) === ResponseCode.EmailSharingDetected && 'errors' in response) {
    yield put(actions.setEmailSharingModalVisible({
      isVisible: true
    }));
  }

  if (wasHarassmentWarningOpen) {
    const harassmentEvent = conversationHarassmentEvent({
      wasEdited: scoredHarassmentMessage !== value,
      conversationId: conversation.id,
      correlationId
    });
    yield put(actions.clearHarassmentMessage());
    yield apply(tracker, tracker.track, [harassmentEvent]);
  }

  if ('errors' in response) {
    var _response$errors2, _response$errors2$;

    yield put(actions.postReplyFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors2 = response.errors) === null || _response$errors2 === void 0 ? void 0 : (_response$errors2$ = _response$errors2[0]) === null || _response$errors2$ === void 0 ? void 0 : _response$errors2$.value) || null
    }));
    return;
  }

  if (conversation.isSuspicious) yield* call(api.deleteConversationSuspicion, conversation.id);
  yield put(actions.postReplySuccess());
  yield put(statelessActions.getConversationRequest({
    conversationId: conversation.id,
    fromPostReply: true
  }));
  const replyEvent = conversationReplyEvent({
    userId,
    itemId: (_conversation$itemId = conversation.itemId) !== null && _conversation$itemId !== void 0 ? _conversation$itemId : null
  });
  yield apply(tracker, tracker.track, [replyEvent]);
}
export function* createConversationThread(_ref6) {
  let {
    payload: {
      recipientId,
      value,
      photoTempUuids
    }
  } = _ref6;
  const userId = yield* select(getUserId);
  const wasHarassmentWarningOpen = yield* select(getWasHarassmentWarningOpen);
  const scoredHarassmentMessage = yield* select(getScoredHarassmentMessage);
  const correlationId = yield* select(getScoredHarassmentMessageCorrelationId);
  if (!userId) return;
  const response = yield* call(api.createConversationThread, {
    recipientId,
    photoTempUuids
  });

  if ((response === null || response === void 0 ? void 0 : response.code) === ResponseCode.EmailSharingDetected && 'errors' in response) {
    yield put(actions.setEmailSharingModalVisible({
      isVisible: true
    }));
  }

  if ((response === null || response === void 0 ? void 0 : response.code) === ResponseCode.HarassmentInMessageDetected && 'errors' in response) {
    var _response$payload$cor2, _response$payload2;

    yield put(actions.setHarassmentMessage({
      messageText: value || '',
      correlationId: (_response$payload$cor2 = (_response$payload2 = response.payload) === null || _response$payload2 === void 0 ? void 0 : _response$payload2.correlation_id) !== null && _response$payload$cor2 !== void 0 ? _response$payload$cor2 : null
    }));
    return;
  }

  if (wasHarassmentWarningOpen) {
    const conversation = yield* select(selectConversation);
    const harassmentEvent = conversationHarassmentEvent({
      wasEdited: scoredHarassmentMessage !== value,
      conversationId: (conversation === null || conversation === void 0 ? void 0 : conversation.id) || null,
      correlationId
    });
    yield put(actions.clearHarassmentMessage());
    yield apply(tracker, tracker.track, [harassmentEvent]);
  }

  if ('errors' in response) {
    var _response$errors$3;

    yield put(actions.createConversationThreadFailure({
      error: ((_response$errors$3 = response.errors[0]) === null || _response$errors$3 === void 0 ? void 0 : _response$errors$3.value) || null
    }));
    return;
  }

  if ('conversation' in response) {
    const conversationStartEvent = startConversationEvent({
      userId,
      itemId: null
    });
    yield* call(api.postReply, {
      conversationId: response.conversation.id,
      body: value,
      photoTempUuids
    });
    yield apply(tracker, tracker.track, [conversationStartEvent]);
    navigateToPage(CONVERSATION_URL(response.conversation.id));
  }
}
export function* createItemConversationThread(_ref7) {
  let {
    payload: {
      itemId,
      sellerId,
      initiator
    }
  } = _ref7;
  const response = yield* call(api.createItemConversationThread, {
    itemId,
    initiator,
    receiverId: sellerId
  });

  if ('errors' in response) {
    var _response$errors$4;

    yield put(actions.createItemConversationThreadFailure({
      error: ((_response$errors$4 = response.errors[0]) === null || _response$errors$4 === void 0 ? void 0 : _response$errors$4.value) || null
    }));
    return;
  }

  if (!('conversation' in response)) return;
  const conversation = transformConversationResponse(response.conversation);
  yield put(actions.createItemConversationThreadSuccess({
    conversation
  }));
}
export function* enterTrackingCode(_ref8) {
  let {
    payload: {
      transactionId,
      trackingCode
    }
  } = _ref8;
  const response = yield* call(api.enterTrackingCode, {
    transactionId,
    trackingCode
  });

  if ('errors' in response) {
    var _response$errors3, _response$errors3$;

    yield put(actions.enterTrackingCodeUiFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors3 = response.errors) === null || _response$errors3 === void 0 ? void 0 : (_response$errors3$ = _response$errors3[0]) === null || _response$errors3$ === void 0 ? void 0 : _response$errors3$.value) || null
    }));
  } else {
    yield put(actions.enterTrackingCodeSuccess());
  }
}
export function* markAsShipped(_ref9) {
  let {
    payload: {
      transactionId,
      proofPhotoUuid
    }
  } = _ref9;
  const response = yield* call(api.markAsShipped, {
    transactionId,
    proofPhotoUuid
  });

  if ('errors' in response) {
    var _response$errors$5;

    yield put(actions.markAsShippedUiFailure({
      error: ((_response$errors$5 = response.errors[0]) === null || _response$errors$5 === void 0 ? void 0 : _response$errors$5.value) || null
    }));
    return;
  }

  yield put(actions.markAsShippedSuccess());
}
export function* setSelectedDropOffTypeMethod(_ref10) {
  let {
    payload: {
      transactionId,
      dropOffTypeId
    }
  } = _ref10;
  const response = yield* call(api.setSelectedDropOffTypeMethod, {
    transactionId,
    dropOffTypeId
  });

  if ('errors' in response) {
    var _response$errors$6;

    yield put(actions.setSelectedDropOffTypeMethodFailure({
      error: ((_response$errors$6 = response.errors[0]) === null || _response$errors$6 === void 0 ? void 0 : _response$errors$6.value) || null
    }));
    return;
  }

  yield call(getShipmentInstructions, {
    type: statelessActions.getShipmentInstructionsRequest.type,
    payload: {
      transactionId
    }
  });
  yield put(actions.setSelectedDropOffTypeMethodSuccess());
}
export function* getDigitalLabel(_ref11) {
  let {
    payload: {
      transactionId
    }
  } = _ref11;
  const response = yield* call(api.getDigitalLabel, {
    transactionId
  });
  if ('errors' in response) return;
  yield put(actions.getDigitalLabelSuccess(transformDigitalLabel(response.digital_label)));
}
export function* createOffer(_ref12) {
  let {
    payload: {
      price,
      isBuyer,
      currency,
      transactionId
    }
  } = _ref12;
  const apiCall = isBuyer ? api.createOfferRequest : api.createOffer;
  const response = yield* call(apiCall, {
    transactionId,
    price,
    currency
  });

  if ('errors' in response) {
    var _response$errors4, _response$errors4$;

    yield put(actions.createOfferFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors4 = response.errors) === null || _response$errors4 === void 0 ? void 0 : (_response$errors4$ = _response$errors4[0]) === null || _response$errors4$ === void 0 ? void 0 : _response$errors4$.value) || null
    }));
  } else {
    yield put(actions.createOfferSuccess());
  }
}
export function* acceptOffer(_ref13) {
  let {
    payload: {
      transactionId,
      offerRequestId
    }
  } = _ref13;
  const response = yield* call(api.acceptOffer, {
    transactionId,
    offerRequestId
  });

  if ('errors' in response) {
    var _response$errors5, _response$errors5$;

    yield put(actions.acceptOfferFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors5 = response.errors) === null || _response$errors5 === void 0 ? void 0 : (_response$errors5$ = _response$errors5[0]) === null || _response$errors5$ === void 0 ? void 0 : _response$errors5$.value) || null
    }));
  } else {
    yield put(actions.acceptOfferSuccess());
  }
}
export function* rejectOffer(_ref14) {
  let {
    payload: {
      transactionId,
      offerRequestId
    }
  } = _ref14;
  const response = yield* call(api.rejectOffer, {
    transactionId,
    offerRequestId
  });

  if ('errors' in response) {
    var _response$errors6, _response$errors6$;

    yield put(actions.rejectOfferFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors6 = response.errors) === null || _response$errors6 === void 0 ? void 0 : (_response$errors6$ = _response$errors6[0]) === null || _response$errors6$ === void 0 ? void 0 : _response$errors6$.value) || null
    }));
  } else {
    yield put(actions.rejectOfferSuccess());
  }
}
export function* getCourierPickUpDates(_ref15) {
  let {
    payload: {
      transactionId
    }
  } = _ref15;
  const response = yield* call(api.getCourierPickUpDates, transactionId);

  if ('errors' in response) {
    var _response$errors7, _response$errors7$;

    yield put(actions.getCourierPickUpDatesFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors7 = response.errors) === null || _response$errors7 === void 0 ? void 0 : (_response$errors7$ = _response$errors7[0]) === null || _response$errors7$ === void 0 ? void 0 : _response$errors7$.value) || null
    }));
  } else {
    const courierPickUpDates = transformPickUpDatesDto(response.pick_up_dates);
    yield put(actions.getCourierPickUpDatesSuccess(courierPickUpDates));
  }
}
export function* getOfferRequestOptions(_ref16) {
  let {
    payload: {
      itemPrice
    }
  } = _ref16;
  const response = yield* call(api.getOfferRequestOptions, {
    itemPrice
  });

  if ('errors' in response) {
    var _response$errors8, _response$errors8$;

    yield put(actions.getOfferRequestOptionsFailure({
      error: response === null || response === void 0 ? void 0 : (_response$errors8 = response.errors) === null || _response$errors8 === void 0 ? void 0 : (_response$errors8$ = _response$errors8[0]) === null || _response$errors8$ === void 0 ? void 0 : _response$errors8$.value
    }));
    return;
  }

  yield put(actions.getOfferRequestOptionsSuccess({
    offerRequestOptions: transformOfferRequestOptions(response.request_options)
  }));
}
export function* fetchTransaction(_ref17) {
  let {
    payload
  } = _ref17;
  const response = yield* call(api.getTransaction, {
    id: payload.transactionId
  });

  if ('errors' in response) {
    var _response$errors9, _response$errors9$;

    yield put(actions.fetchTransactionRequestFailure({
      error: response === null || response === void 0 ? void 0 : (_response$errors9 = response.errors) === null || _response$errors9 === void 0 ? void 0 : (_response$errors9$ = _response$errors9[0]) === null || _response$errors9$ === void 0 ? void 0 : _response$errors9$.value
    }));
    return;
  }

  const transaction = transformTransaction(response.transaction);
  yield put(actions.fetchTransactionRequestSuccess({
    transaction
  }));
}
export function* fetchShippingInstructions(_ref18) {
  let {
    payload: {
      transactionId
    }
  } = _ref18;
  const response = yield* call(api.getShippingInstructions, transactionId);

  if ('errors' in response) {
    var _response$errors10, _response$errors10$;

    yield put(actions.fetchShippingInstructionsRequestFailure({
      error: (response === null || response === void 0 ? void 0 : (_response$errors10 = response.errors) === null || _response$errors10 === void 0 ? void 0 : (_response$errors10$ = _response$errors10[0]) === null || _response$errors10$ === void 0 ? void 0 : _response$errors10$.value) || null
    }));
    return;
  }

  const shippingInstructions = transformShippingInstructionsDto(response.shipping_instructions);
  yield put(actions.fetchShippingInstructionsRequestSuccess({
    shippingInstructions
  }));
}
export default function* saga() {
  yield takeEvery(statelessActions.translateConversationRequest, translateConversation);
  yield takeEvery(statelessActions.getConversationRequest, getConversation);
  yield takeEvery(actions.getTrackingJourneySummaryRequest, getTrackingJourneySummary);
  yield takeEvery(statelessActions.getShipmentInstructionsRequest, getShipmentInstructions);
  yield takeEvery(actions.postReplyRequest, postReply);
  yield takeEvery(actions.createConversationThreadRequest, createConversationThread);
  yield takeEvery(actions.createItemConversationThreadRequest, createItemConversationThread);
  yield takeEvery(actions.enterTrackingCodeRequest, enterTrackingCode);
  yield takeEvery(actions.markAsShippedRequest, markAsShipped);
  yield takeEvery(actions.setSelectedDropOffTypeMethodRequest, setSelectedDropOffTypeMethod);
  yield takeEvery(statelessActions.getDigitalLabelRequest, getDigitalLabel);
  yield takeEvery(actions.createOfferRequest, createOffer);
  yield takeEvery(actions.acceptOfferRequest, acceptOffer);
  yield takeEvery(actions.rejectOfferRequest, rejectOffer);
  yield takeEvery(actions.getCourierPickUpDatesRequest, getCourierPickUpDates);
  yield takeEvery(actions.getOfferRequestOptionsRequest, getOfferRequestOptions);
  yield takeEvery(actions.fetchTransactionRequest, fetchTransaction);
  yield takeEvery(actions.fetchShippingInstructionsRequest, fetchShippingInstructions);
}